import dayjs from 'dayjs'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { DatePicker } from '#/shared/ui/date-picker'
import { AlarmClockIcon, CloseIcon } from '#/shared/ui/icons'
import { Popover } from '#/shared/ui/popover'
import { Skeleton } from '#/shared/ui/skeleton'
import type { TaskPriority, TaskValue } from '#/shared/ui/task-editor'
import { TimePicker, formatTimeLabel } from '#/shared/ui/time-picker'
import { VoiceInputButton } from '#/shared/ui/voice-recording-island'

import { ActiveTasks } from './active-tasks'
import type { PanelState } from './panel-state'

type Task = PanelState['task']

const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high']
const SKELETON_ROWS = 3

const PRIORITY_ACTIVE: Record<TaskPriority, string> = {
  low: 'border-green-100 bg-green-50 text-green-500',
  medium: 'border-amber-200 bg-amber-100 text-amber-700',
  high: 'border-red-200 bg-red-100 text-red-500',
}

function Field({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <p className="text-xs leading-none text-warm-500 select-none">{label}</p>
      {children}
    </div>
  )
}

function SetTimeToggle({
  date,
  time,
  open,
  onClick,
}: {
  date: Date | undefined
  time: string
  open: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  const summary = date
    ? `${dayjs(date).format('MMM D')}${time ? ` · ${formatTimeLabel(time)}` : ''}`
    : t('editor.task.setTime')
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onClick}
      className={cn(
        'flex shrink-0 cursor-pointer items-center gap-1.5 text-sm transition-colors select-none',
        date ? 'text-gray-900' : 'text-gray-300 hover:text-gray-500',
        open && 'text-primary',
      )}
    >
      <AlarmClockIcon className="size-4" />
      {summary}
    </button>
  )
}

function TaskTitleField({
  value,
  patch,
}: {
  value: TaskValue
  patch: (partial: Partial<TaskValue>) => void
}) {
  const { t } = useTranslation()
  const [showTime, setShowTime] = React.useState(value.date !== undefined)
  return (
    <Field label={t('editor.task.fields.task')}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.25">
          <input
            type="text"
            value={value.task}
            onChange={(e) => patch({ task: e.target.value })}
            placeholder={t('editor.task.placeholder')}
            className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none"
          />
          <VoiceInputButton
            value={value.task}
            onChange={(task) => patch({ task })}
          />
          <SetTimeToggle
            date={value.date}
            time={value.time}
            open={showTime}
            onClick={() => setShowTime((prev) => !prev)}
          />
        </div>
        {showTime && (
          <div className="flex animate-in items-start gap-2 fade-in slide-in-from-top-1 duration-200 ease-out">
            <div className="min-w-0 flex-1">
              <DatePicker
                value={value.date}
                onValueChange={(date) => patch({ date })}
                dateFormat="MMM D, YYYY"
              />
            </div>
            <div className="min-w-0 flex-1">
              <TimePicker
                portal
                value={value.time}
                onChange={(time) => patch({ time })}
                placeholder={t('editor.task.setTime')}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>
    </Field>
  )
}

function PriorityPills({
  value,
  onChange,
}: {
  value: TaskPriority
  onChange: (next: TaskPriority) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-7 items-stretch">
      {PRIORITIES.map((priority, i) => {
        const active = value === priority
        return (
          <button
            key={priority}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(priority)}
            className={cn(
              'relative flex cursor-pointer items-center border px-3 text-sm-minus transition-colors select-none',
              i === 0 && 'rounded-l-full',
              i === PRIORITIES.length - 1 && 'rounded-r-full',
              i > 0 && '-ml-px',
              active
                ? cn('z-10', PRIORITY_ACTIVE[priority])
                : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
            )}
          >
            {t(`editor.task.priorities.${priority}`)}
          </button>
        )
      })}
    </div>
  )
}

function UserOptionList({
  task,
  onToggle,
}: {
  task: Task
  onToggle: (value: string) => void
}) {
  const { t } = useTranslation()
  if (task.usersLoading) {
    return (
      <div className="flex flex-col gap-1 p-1">
        {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full rounded-sm" />
        ))}
      </div>
    )
  }
  if (task.userOptions.length === 0) {
    return (
      <p className="px-2.5 py-2 text-sm text-gray-500">
        {t('editor.task.users.empty')}
      </p>
    )
  }
  return (
    <ul className="flex max-h-50 flex-col overflow-auto p-1">
      {task.userOptions.map((option) => {
        const selected = task.value.users.includes(option.value)
        return (
          <li key={option.value}>
            <button
              type="button"
              onClick={() => onToggle(option.value)}
              className="flex h-9 w-full cursor-pointer items-center gap-2.5 rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100"
            >
              <span
                className={cn(
                  'size-4 shrink-0 rounded-sm border',
                  selected ? 'border-primary bg-primary' : 'border-gray-200',
                )}
              />
              <span className="flex-1 truncate">{option.label}</span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function AddUserPopover({
  task,
  onToggle,
}: {
  task: Task
  onToggle: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <Popover
      portal
      side="auto"
      align="auto"
      contentClassName="w-60 rounded-md border border-gray-200 bg-white p-1 shadow-popover"
      trigger={(open) => (
        <span
          className={cn(
            'cursor-pointer text-sm text-gray-300 transition-colors select-none hover:text-gray-500',
            open && 'text-primary',
          )}
        >
          {t('editor.task.addUser')}
        </span>
      )}
    >
      {() => (
        <div className="flex flex-col gap-1">
          <input
            value={task.userSearch}
            onChange={(e) => task.onUserSearchChange(e.target.value)}
            placeholder={t('editor.task.searchUser')}
            className="mx-1 mt-1 h-8 rounded-sm border border-gray-200 px-2.5 text-sm placeholder:text-gray-300 focus:outline-none"
          />
          <UserOptionList task={task} onToggle={onToggle} />
        </div>
      )}
    </Popover>
  )
}

function AssigneeField({ task }: { task: Task }) {
  const { t } = useTranslation()
  const users = task.value.users
  const setUsers = (next: string[]) =>
    task.onChange({ ...task.value, users: next })
  const selected = task.userOptions.filter((o) => users.includes(o.value))
  const toggle = (value: string) =>
    setUsers(
      users.includes(value)
        ? users.filter((u) => u !== value)
        : [...users, value],
    )
  return (
    <div className="flex flex-wrap items-center gap-2">
      {selected.map((option) => (
        <span
          key={option.value}
          className="flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-sm-minus text-gray-500 select-none"
        >
          {option.label}
          <button
            type="button"
            onClick={() => toggle(option.value)}
            aria-label={t('editor.task.removeUser', { name: option.label })}
            className="flex size-3 cursor-pointer items-center justify-center text-gray-400 transition-colors hover:text-gray-900"
          >
            <CloseIcon className="size-2" />
          </button>
        </span>
      ))}
      <AddUserPopover task={task} onToggle={toggle} />
    </div>
  )
}

export function CompactTaskForm({
  task,
  shipmentId,
}: {
  task: Task
  shipmentId: string | undefined
}) {
  const { t } = useTranslation()
  const value = task.value
  const patch = (partial: Partial<TaskValue>) =>
    task.onChange({ ...value, ...partial })
  const canSave = value.task.trim().length > 0 && !task.isSaving
  return (
    <div className="flex flex-col border-b border-gray-200">
      <div className="flex flex-col gap-4 p-4">
        <TaskTitleField value={value} patch={patch} />
        <div className="flex items-start gap-4">
          <Field label={t('editor.task.fields.priority')}>
            <PriorityPills
              value={value.priority}
              onChange={(priority) => patch({ priority })}
            />
          </Field>
          <Field
            label={t('editor.task.fields.assignee')}
            className="min-w-0 flex-1"
          >
            <AssigneeField task={task} />
          </Field>
        </div>
      </div>
      <ActiveTasks shipmentId={shipmentId} />
      <div className="sticky bottom-0 z-10 flex h-14 items-center justify-end gap-2.5 border-t border-gray-200 bg-gray-100 px-4">
        <Button
          variant="outline"
          size="xs"
          disabled={task.isSaving}
          onClick={task.onCancel}
        >
          {t('editor.task.cancel')}
        </Button>
        <Button
          size="xs"
          disabled={!canSave}
          onClick={() => void task.onSave(value)}
        >
          {task.isSaving ? t('table.saving') : t('editor.task.saveShort')}
        </Button>
      </div>
    </div>
  )
}
